import React from "react";
import { useNavigate } from "react-router-dom";

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <div className="w-full h-screen overflow-y-scroll bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-6 pb-16">
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            {/* Large 404 with gradient effect */}
            <div className="mb-4 relative">
              <h1 className="text-5xl md:text-6xl font-extrabold mb-1">
                <span
                  className="bg-clip-text text-transparent bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700"
                  style={{
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                  }}
                >
                  404
                </span>
              </h1>
              <div className="absolute inset-0 blur-2xl opacity-20">
                <h1 className="text-6xl md:text-7xl font-extrabold text-primary-500">
                  404
                </h1>
              </div>
            </div>

            {/* Main heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Page Not Found
            </h2>

            {/* Description text */}
            <div className="mb-7 max-w-xl">
              <p className="text-lg text-gray-600 mb-1.5 font-medium">
                Oops! The page you're looking for doesn't exist.
              </p>
              <p className="text-sm text-gray-500">
                The URL you entered may be incorrect or the page may have been
                moved. Don't worry, let's get you back on track!
              </p>
            </div>

            {/* Main action card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6 mb-6 max-w-md w-full transform transition-all hover:shadow-xl">
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary-100 rounded-full blur-xl opacity-50"></div>
                  <div className="relative bg-gradient-to-br from-primary-50 to-primary-100 rounded-full p-3">
                    <svg
                      className="w-10 h-10 text-primary-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Let's Get You Back
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                Return to the home page to continue exploring our AI-powered
                fashion services including virtual try-on, model generation, and
                more.
              </p>

              <button
                onClick={() => navigate("/")}
                className="w-full px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 text-sm"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Go to Home Page
              </button>
            </div>

            {/* Quick links */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
              <div className="flex items-center gap-1.5 text-gray-600">
                <svg
                  className="w-3.5 h-3.5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Need help?</span>
              </div>
              <a
                href="/contact-us"
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors flex items-center gap-1 group"
              >
                <span>Contact us</span>
                <svg
                  className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>

          </div>

          {/* Decorative elements */}
          <div className="fixed top-20 left-10 w-24 h-24 bg-primary-100 rounded-full blur-3xl opacity-20 -z-10 pointer-events-none"></div>
          <div className="fixed bottom-20 right-10 w-32 h-32 bg-primary-200 rounded-full blur-3xl opacity-20 -z-10 pointer-events-none"></div>
        </div>
      </div>
    </div>
  );
}
